import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, useWindowDimensions, ScrollView, Image } from 'react-native-web';
import { ActivityIndicator } from 'react-native';
import { useSelector } from 'react-redux';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux'
import { setAccount } from '../store/modules/accounts'

const StripePaymentScreen = () => {
  const stripe = useStripe();
  const elements = useElements();
  const functions = getFunctions(app);
  const account = useSelector((state) => state.accounts.account);
  const { width } = useWindowDimensions();
  const isMobileScreen = width < 768;
  const dispatch = useDispatch()

  const [kanjiName, setKanjiName] = useState('');
  const [kanaName, setKanaName] = useState('');
  const [cardName, setCardName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

// 本番環境用のエンドポイント
const SUBSCRIPTION_ENDPOINT_PROD = 'https://us-central1-trinity-kurose.cloudfunctions.net/createStripeSubscription';
const SETUP_INTENT_ENDPOINT_PROD = 'https://us-central1-trinity-kurose.cloudfunctions.net/createSetupIntent';
// テスト環境用のエンドポイント
const SUBSCRIPTION_ENDPOINT_TEST = 'https://us-central1-trinity-app-test.cloudfunctions.net/createStripeSubscription';
const SETUP_INTENT_ENDPOINT_TEST = 'https://us-central1-trinity-app-test.cloudfunctions.net/createSetupIntent';

// 実際の環境に応じて使い分け
const setupIntentEndpoint = SETUP_INTENT_ENDPOINT_PROD;
const subscriptionEndpoint = SUBSCRIPTION_ENDPOINT_PROD;
//const setupIntentEndpoint = SETUP_INTENT_ENDPOINT_TEST;
//const subscriptionEndpoint = SUBSCRIPTION_ENDPOINT_TEST;

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [loading]);

  // カタカナ専用バリデーション
  const handleKanaBlur = () => {
    const katakanaRegex = /^[\u30A0-\u30FFー　]+$/;
    if (kanaName && !katakanaRegex.test(kanaName)) {
      setKanaName('');
      alert('フリガナ(カタカナ)はカタカナと全角スペースのみ入力してください');
    }
  };

  // 電話番号バリデーション
  const handlePhoneChange = (text) => {
    const filtered = text.replace(/[^0-9]/g, '');
    if (filtered.length <= 13) setPhone(filtered);
  };
  const handlePhoneBlur = () => {
    if (phone && (phone.length < 8 || phone.length > 13)) {
      alert('電話番号は8桁以上13桁以下である必要があります');
      setPhone('');
    }
  };

  // 郵便番号バリデーション
  const handlePostalCodeChange = (text) => {
    setPostalCode(text.replace(/[^0-9]/g, ''));
  };

  // メールアドレスバリデーション
  const handleEmailBlur = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      alert('正しいメールアドレス形式を入力してください');
      setEmail('');
    }
  };

  const navigate = useNavigate();

  const handleSubmit = async () => {
    // 画面トップにスクロール
    window.scrollTo({ top: 0, behavior: 'auto' });
    setLoading(true);
    setError('');

    // 1) 入力チェック
    if (!kanjiName || !kanaName || !email || !phone || !postalCode || !address || !cardName) {
      alert('すべての項目を入力してください');
      setLoading(false);
      return;
    }

    // 2) Stripe Elements 準備チェック
    if (!stripe || !elements) {
      setError('Stripe が読み込まれていません');
      setLoading(false);
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      alert('カード情報入力欄が読み込まれていません。リロードしてください。');
      setLoading(false);
      return;
    }

    // 3) PaymentMethod の作成
    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: cardName,
        email,
        phone,
        address: { line1: address, postal_code: postalCode },
      },
    });
    if (pmError || !paymentMethod?.id) {
      setError(pmError?.message || 'カード情報の取得に失敗しました');
      setLoading(false);
      return;
    }

    // 4) off_session 認証（SetupIntent）—必ず実行—
    let setupData;
    try {
      const setupRes = await fetch(setupIntentEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: account.stripeCustomerId,  // 初回は undefined でも OK
          paymentMethodId: paymentMethod.id,
          email: email,
          name: cardName
        }),
      });
      setupData = await setupRes.json();
      if (!setupData.success) {
        throw new Error(setupData.error || 'カード認証に失敗しました');
      }

      // ① まずオフセッションで試す
      const { error: offErr } = await stripe.confirmCardSetup(
        setupData.clientSecret,
        { payment_method: paymentMethod.id }
      );

      if (offErr) {
        if (offErr.code === 'setup_intent_authentication_failure') {
          const { error: userErr } = await stripe.confirmCardSetup(
            setupData.clientSecret,
            { payment_method: paymentMethod.id }
          );
          if (userErr) {
            throw userErr;
          }
        } else {
          throw offErr;
        }
      }
    } catch (err) {
      console.error('SetupIntent エラー:', err);
      setError(err.message || 'カード認証に失敗しました');
      setLoading(false);
      return;
    }

    // 5) サブスクリプション作成 → 必要なら 3D セキュア認証
    try {
      const subRes = await fetch(subscriptionEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          paymentMethodId: paymentMethod.id,
          uid: account.uid,
          kanjiName,
          kanaName,
          phone,
          postalCode,
          address,
        }),
      });
      const result = await subRes.json();
      if (!result.success) {
        throw new Error(result.error || 'サブスクリプション登録に失敗しました');
      }

      // 6) 初回 customerId を保存（次回 off-session 用）
      if (result.customerId) {
        const updated = { ...account, stripeCustomerId: result.customerId };
        dispatch(setAccount(updated));
        localStorage.setItem('account', JSON.stringify(updated));
      }

      // 7) 3D セキュア認証が必要な場合
      if (result.clientSecret) {
        const { error: confirmErr } = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmErr) throw confirmErr;
      }

      alert('プレミアム会員登録が完了しました');
      navigate('/home');
    } catch (err) {
      console.error('サブスクリプション作成エラー:', err);
      setError(err.message || '通信エラーが発生しました');
    }

    setLoading(false);
  };

  const cardBrands = [
    require('../assets/images/card_visa.png'),
    require('../assets/images/card_mastercard.png'),
    require('../assets/images/card_jcb.png'),
    require('../assets/images/card_amex.png'),
    require('../assets/images/card_diners.png'),
  ];

  return (
    <View style={{ flex: 1, position: 'relative' }}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View style={styles.mainBlock}>
          <Image
            source={require('../assets/images/premium_ryokin.png')}
            style={[styles.ryokinttlImage, { height: width * 0.09 }]}
          />
          <View style={styles.border2} />
          <View style={styles.ryokinBlock}>
            <Image
              source={require('../assets/images/premium_getsugaku.png')}
              style={[styles.nenkanImage, { height: isMobileScreen ? width * 0.12 : width * 0.08 }]}
            />
            <View style={[styles.ryokinBlock2, { width: isMobileScreen ? 'auto' : '80%' }]}>
              <Text style={[styles.Ryokintext, { fontSize: isMobileScreen ? 70 : 100 }]}>200</Text>
              <Text style={styles.RyokintextSmall}>円(税込)</Text>
            </View>
          </View>

          <View style={styles.mainBlockInr}>
            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>お名前（漢字）</Text>
              <TextInput
                style={styles.input}
                value={kanjiName}
                onChangeText={setKanjiName}
                placeholder="山田　太郎"
                placeholderTextColor="#ccc"
              />
            </View>

            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>お名前（カタカナ）</Text>
              <TextInput
                style={styles.input}
                value={kanaName}
                onChangeText={setKanaName}
                onBlur={handleKanaBlur}
                placeholder="ヤマダ　タロウ"
                placeholderTextColor="#ccc"
              />
            </View>

            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>メールアドレス</Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                onBlur={handleEmailBlur}
                keyboardType="email-address"
                placeholder="sample@kurose-pf-trinity.com"
                placeholderTextColor="#ccc"
              />
            </View>

            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>電話番号</Text>
              <TextInput
                style={styles.input}
                value={phone}
                onChangeText={handlePhoneChange}
                onBlur={handlePhoneBlur}
                keyboardType="phone-pad"
                placeholder="09012345678"
                placeholderTextColor="#ccc"
              />
            </View>

            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>郵便番号</Text>
              <TextInput
                style={styles.input}
                value={postalCode}
                onChangeText={handlePostalCodeChange}
                keyboardType="numeric"
                placeholder="1234567"
                placeholderTextColor="#ccc"
              />
            </View>

            <View style={styles.InputBlock}>
              <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>住所</Text>
              <TextInput
                style={styles.input}
                value={address}
                onChangeText={setAddress}
                placeholder="広島県福山市神辺町川南396-1"
                placeholderTextColor="#ccc"
              />
            </View>

            <Text style={[
                styles.InputBlockHeader,
                  { fontSize: isMobileScreen ? 14 : 18 },
                ]}
              >
                カード情報入力
              </Text>
              <View
                style={[
                  styles.CardBlock,
                    { paddingHorizontal: isMobileScreen ? 20 : 20 },
                    { paddingVertical: isMobileScreen ? 30 : 30 },
                  ]}
              >
                <View style={styles.cardBrandRow}>
                  {cardBrands.map((src, index) => (
                    <Image
                      key={index}
                      source={src}
                      style={[
                        styles.cardBrandImage,
                          { width: isMobileScreen ? 43 : 60 },
                          { height: isMobileScreen ? 43 : 60 },
                        ]}
                      resizeMode="contain"
                    />
                  ))}
                </View>
                <View style={styles.InputBlock}>
                  <View style={styles.InputBorder}>
                    <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>
                      カード名義人（ローマ字）
                    </Text>
                    <TextInput
                      style={styles.input}
                      value={cardName}
                      onChangeText={(text) => {
                        const filtered = text.replace(/[^A-Za-z0-9\s]/g, '');
                        setCardName(filtered);
                      }}
                      autoCapitalize="characters"
                      placeholder="TARO YAMADA"
                      placeholderTextColor="#ccc"
                    />
                  </View>
                </View>
                <View style={styles.InputBorder}>
                  <Text style={[styles.label, { fontSize: isMobileScreen ? 14 : 18 }]}>カード情報</Text>
                  <View style={styles.cardElementWrapper}>
                    <CardElement options={{
                      style: {
                        base: {
                          fontSize: isMobileScreen ? '14px' : '16px',
                          '::placeholder': { color: '#ccc' },
                          color: '#000',
                          padding: '11px',
                          border: 'none',
                          backgroundColor: '#ffffff',
                          borderRadius: '10px',
                          fontFamily: 'inherit',
                        },
                        invalid: { color: '#fa755a' },
                      },
                      hidePostalCode: true,
                    }} />
                  </View>
                  {error && <Text style={styles.error}>{error}</Text>}
                </View>
              </View>
            <TouchableOpacity
              onPress={handleSubmit}
              style={[styles.button, loading && { backgroundColor: '#ccc' }]}
              disabled={loading}
            >
              <Text style={styles.buttonText}>{loading ? '登録中...' : 'とりにてぃプレミアムに登録'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
      {loading && (
        <View style={styles.overlay}>
          <View style={styles.overlayInner}>
            <ActivityIndicator size="large" color="#cd2829" />
            <Text style={{ color: '#555', marginTop: 10, textAlign: 'center' }}>認証に30秒ほどかかります。{'\n'}そのままお待ちください。</Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: 'rgb(238, 238, 238)',
  },
  mainBlock: {
    paddingHorizontal: 20,
    paddingVertical: 50,
    backgroundColor: 'rgb(238, 238, 238)',
  },
  mainBlockInr: {
    maxWidth: 800,
    paddingHorizontal: 10,
    width: '100%',
    marginHorizontal: 'auto',
  },
  ryokinttlImage: {
    maxWidth: 450,
    width: '100%',
    maxHeight: 80,
    alignSelf: 'center',
    resizeMode: 'contain',
    marginBottom: 10,
  },
  border2: {
    backgroundColor: '#cd2829',
    height: 0.6,
    maxWidth: 450,
    alignSelf: 'center',
    width: '100%',
    marginBottom: 20,
  },
  ryokinBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
    justifyContent: 'center',
    marginBottom: 30,
  },
  nenkanImage: {
    width: '15%',
    maxWidth: 110,
    maxHeight: 110,
    resizeMode: 'contain',
  },
  ryokinBlock2: {
    maxWidth: 240,
    flexDirection: 'row',
    width: '80%',
    alignItems: 'baseline',
  },
  Ryokintext: {
    fontSize: 100,
    color: '#555555',
  },
  RyokintextSmall: {
    fontSize: 16,
    color: '#555555',
  },
  InputBlock: {
    marginBottom: 20,
  },
  label: {
    marginBottom: 10,
    color: '#8d8d8d',
  },
  input: {
    padding: 10,
    borderRadius: 10,
    backgroundColor: '#ffffff',
    fontSize: 16,
  },
  cardElementWrapper: {
    padding: 12,
    borderWidth: 0,
    borderColor: 'transparent',
    borderRadius: 10,
    backgroundColor: '#fff',
  },
  button: {
    backgroundColor: '#cd2829',
    paddingHorizontal: 30,
    paddingVertical: 25,
    borderRadius: 50,
    alignItems: 'center',
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
  error: {
    color: '#cd2829',
    marginBottom: 10,
  },
  InputBlockHeader:{
    marginBottom: 10,
    color: '#8d8d8d',
  },
  CardBlock: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    marginBottom: 30,
  },
  InputBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#555555',
    borderStyle: 'solid',
    paddingBottom: 5,
  },
  cardBrandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  loadingText: {
    textAlign: 'center',
    color: '#555555',
    marginBottom: 10,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgb(242, 242, 242)',
    zIndex: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  overlayInner: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default StripePaymentScreen;
